/**
 * Collapsed-row data preview (D-99.2).
 *
 * Sources a muted preview of recorded row names for a section's collapsed header:
 * "TD Canada Trust · RBC Royal Bank". Pure + framework-free so the join/format
 * rules are unit-testable independently of the row.
 *
 * The identifying field per section is a curated override
 * (`RECORD_FIELD_BY_SECTION`) when one exists, else the section's first basic
 * field (`basicFieldKeys[0]`). The caller reads each user-touched row's value for
 * that field, formats it (`formatPreviewValue`), then composes the line
 * (`composeSectionPreview`). Empty sections produce "" — no placeholder text.
 */
import { parseAddress, formatAddressSummary, streetSegment } from "@shared/address";
import { identityKeysFromFields, identityKeyFromFields, renderIdentityValue, type IdentityDeclaringField } from "@shared/constants/record-fields";
import { formatSelectWithSpecify } from "@shared/constants/specify-on-other";

/** s199: identity is read off the section's own FIELD ROWS (`identityOrdinal`), which arrive in
 *  the payload this page already fetches. The slug-keyed map they used to consult is deleted.
 *  Re-exported so existing import sites keep working — one implementation, not two (G7). */
export { identityKeysFromFields, identityKeyFromFields };

/** s110: on a LOCKED row, the section's IDENTITY field is frozen (read-only) — the seeded identity
 *  (e.g. Gov IDs `id_type`) must not change, while every other field (e.g. `person_name`) stays
 *  editable. Replaces the old positional `idx === 0` rule, which broke on Gov IDs after s108 moved
 *  `person_name` to lead the ribbon (freezing the wrong field).
 *
 *  s199: keyed on the section's DECLARED identity, read from its field rows, rather than on a
 *  slug-keyed map plus a ribbon-position fallback. Same answer, one source. */
export function isLockedIdentityField(
  fieldKey: string,
  fields: readonly IdentityDeclaringField[] | undefined,
): boolean {
  const identity = identityKeyFromFields(fields);
  return identity !== undefined && fieldKey === identity;
}

/** Format one identifying-field raw value for the preview: address-typed values
 *  (structured JSON) collapse to their street segment; everything else is shown
 *  verbatim (trimmed). Non-address values with commas are NOT truncated.
 *  OTHER-SPECIFY-1 (s155): when the identity field is a specifyOnOther select answered
 *  "Other" (e.g. poa_type, doc_type, document), the captured detail reads "Other — {text}"
 *  so the record isn't labelled a bare "Other". `formatSelectWithSpecify` no-ops otherwise. */
export function formatPreviewValue(raw: string | null | undefined, specifyText?: string | null): string {
  const v = (raw ?? "").trim();
  if (!v) return "";
  // s199 ruling 4: the address rule is the SHARED `renderIdentityValue`, not a second copy. This
  // function keeps only what is genuinely its own — the OTHER-SPECIFY-1 detail suffix.
  const rendered = renderIdentityValue(v);
  if (rendered !== v) return rendered;
  return formatSelectWithSpecify(v, specifyText ?? null);
}

/** Compose the collapsed-row preview from per-record labels (already in display
 *  order; blanks are dropped). Joins up to `max` with " · " and appends
 *  "+N more" for the overflow. Empty input → "" (no italic placeholder — D-99.2). */
export function composeSectionPreview(labels: Array<string | null | undefined>, max = 3): string {
  const clean = labels.map((l) => (l ?? "").trim()).filter(Boolean);
  if (clean.length === 0) return "";
  if (clean.length <= max) return clean.join(" · ");
  return `${clean.slice(0, max).join(" · ")} +${clean.length - max} more`;
}
